// broadcast_v1 / v2: mass-message (LINE) — compose (text+image, audience incl. manual picker, schedule) + history (retry/cancel)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';
import toast from 'react-hot-toast';
import ImageCropModal from '../components/ImageCropModal'; // img_crop_v1

const AUD = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'tag', label: 'ตามแท็ก' },
  { key: 'recent', label: 'คุยล่าสุด' },
  { key: 'manual', label: 'เลือกเอง' },
];

const STATUS_BADGE = {
  scheduled: { t: 'ตั้งเวลา', c: '#5b21b6', b: '#ede9fe' },
  pending: { t: 'รอส่ง', c: '#92400e', b: '#fef3c7' },
  sending: { t: 'กำลังส่ง', c: '#1e40af', b: '#dbeafe' },
  done: { t: 'สำเร็จ', c: '#166534', b: '#dcfce7' },
  partial: { t: 'สำเร็จบางส่วน', c: '#9a3412', b: '#ffedd5' },
  failed: { t: 'ล้มเหลว', c: '#991b1b', b: '#fee2e2' },
  cancelled: { t: 'ยกเลิกแล้ว', c: '#475569', b: '#f1f5f9' },
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 };
const btn = (bg, disabled) => ({
  background: disabled ? '#cbd5e1' : bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 18px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14,
});
const smallBtn = (border, color, bg) => ({
  padding: '6px 12px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
  border: '1px solid ' + border, color, background: bg,
});

function nowThaiLocal() {
  // "YYYY-MM-DDTHH:mm" in Thai wall-clock, for datetime-local min
  return new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 16);
}
function thaiLocalToISO(v) {
  // treat datetime-local value as Thai time -> UTC ISO
  let s = v;
  if (s.length === 16) s += ':00';
  return new Date(s + '+07:00').toISOString();
}

export default function Broadcast() {
  const [tab, setTab] = useState('compose');
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [audType, setAudType] = useState('all');
  const [allTags, setAllTags] = useState([]);
  const [selTags, setSelTags] = useState([]);
  const [days, setDays] = useState(30);
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null); // img_crop_v1: ไฟล์ที่กำลังครอป (การ์ด)
  const [origFile, setOrigFile] = useState(null); // เก็บต้นฉบับไว้ครอปใหม่
  // v3 card mode
  const [msgType, setMsgType] = useState('text'); // text | card | rich (rich_msg_v1)
  const [cTitle, setCTitle] = useState('');
  const [cDetail, setCDetail] = useState('');
  const [cBtnLabel, setCBtnLabel] = useState('');
  const [cBtnUrl, setCBtnUrl] = useState('');
  const [richLink, setRichLink] = useState(''); // rich_msg_v1: ลิงก์เมื่อกดที่รูปเต็ม
  // manual picker
  const [custList, setCustList] = useState([]);
  const [custQ, setCustQ] = useState('');
  const [selCust, setSelCust] = useState([]); // [{id,name}]
  // schedule
  const [schedOn, setSchedOn] = useState(false);
  const [schedAt, setSchedAt] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);
  const custTimer = useRef(null);

  const audFilter = useCallback(() => {
    if (audType === 'tag') return { tags: selTags };
    if (audType === 'recent') return { days: Number(days) || 30 };
    if (audType === 'manual') return { customer_ids: selCust.map((c) => c.id) };
    return {};
  }, [audType, selTags, days, selCust]);

  useEffect(() => {
    API.get('/api/broadcasts/channels')
      .then((r) => { const chs = r.data.channels || []; setChannels(chs); if (chs.length) setChannelId(chs[0].id); })
      .catch(() => toast.error('โหลดช่องทางไม่สำเร็จ'));
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!channelId) return;
    API.get('/api/broadcasts/tags', { params: { channel_id: channelId } }).then((r) => setAllTags(r.data.tags || [])).catch(() => setAllTags([]));
    setPreview(null); setSelCust([]); setCustList([]);
  }, [channelId]);

  useEffect(() => { setPreview(null); }, [audType, selTags, days, selCust]);

  // manual customer search (debounced)
  useEffect(() => {
    if (audType !== 'manual' || !channelId) return;
    if (custTimer.current) clearTimeout(custTimer.current);
    custTimer.current = setTimeout(() => {
      API.get('/api/broadcasts/customers', { params: { channel_id: channelId, q: custQ, limit: 100 } })
        .then((r) => setCustList(r.data.customers || [])).catch(() => setCustList([]));
    }, 300);
  }, [audType, channelId, custQ]);

  function loadHistory() {
    API.get('/api/broadcasts').then((r) => {
      const list = r.data.broadcasts || [];
      setHistory(list);
      const active = list.some((b) => b.status === 'pending' || b.status === 'sending');
      if (active && !pollRef.current) pollRef.current = setInterval(pollActive, 3000);
      else if (!active && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }).catch(() => {});
  }
  function pollActive() {
    API.get('/api/broadcasts').then((r) => {
      const list = r.data.broadcasts || [];
      setHistory(list);
      if (!list.some((b) => b.status === 'pending' || b.status === 'sending') && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }).catch(() => {});
  }

  // img_crop_v1: อัปโหลดไฟล์/blob ขึ้น /api/upload
  async function uploadFile(fileOrBlob, name) {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', fileOrBlob, name || fileOrBlob.name || 'image.jpg');
      const r = await API.post('/api/upload', fd);
      setImageUrl(r.data.url); setPreview(null);
    } catch (e) { toast.error('อัปโหลดรูปไม่สำเร็จ'); } finally { setUploading(false); }
  }

  async function onImage(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (!/\.(jpe?g|png)$/i.test(f.name)) return toast.error('รองรับเฉพาะ JPG / PNG');
    // img_crop_v1: การ์ด hero ต้องเป็น 20:13 -> เปิดหน้าครอปก่อน; ข้อความปกติอัปตรง
    if (msgType === 'card') { setOrigFile(f); setCropFile(f); return; }
    await uploadFile(f);
  }

  // img_crop_v1: ได้ blob ครอปแล้ว -> อัปโหลด
  async function onCropped(blob) {
    setCropFile(null);
    await uploadFile(blob, 'card.jpg');
  }

  async function doPreview() {
    if (!channelId) return toast.error('เลือกช่องทางก่อน');
    if (audType === 'tag' && selTags.length === 0) return toast.error('เลือกแท็กอย่างน้อย 1 อัน');
    if (audType === 'manual' && selCust.length === 0) return toast.error('เลือกลูกค้าอย่างน้อย 1 คน');
    setPreviewing(true);
    try {
      const r = await API.post('/api/broadcasts/preview', { channel_id: channelId, audience_type: audType, audience_filter: audFilter() });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.error || 'ประเมินผู้รับไม่สำเร็จ'); } finally { setPreviewing(false); }
  }

  async function doSend() {
    if (msgType === 'card') {
      if (!cTitle.trim() && !cDetail.trim() && !imageUrl) return toast.error('การ์ดต้องมีหัวข้อ รายละเอียด หรือรูป');
      if ((cBtnLabel.trim() && !cBtnUrl.trim()) || (!cBtnLabel.trim() && cBtnUrl.trim())) return toast.error('ปุ่มต้องมีทั้งข้อความปุ่มและลิงก์');
      if (cBtnUrl.trim() && !/^https?:\/\//i.test(cBtnUrl.trim())) return toast.error('ลิงก์ปุ่มต้องขึ้นต้นด้วย http:// หรือ https://');
    } else if (msgType === 'rich') {
      if (!imageUrl) return toast.error('ริชเมสเสจต้องแนบรูปก่อน');
      if (richLink.trim() && !/^https?:\/\//i.test(richLink.trim())) return toast.error('ลิงก์ต้องขึ้นต้นด้วย http:// หรือ https://');
    } else if (!message.trim() && !imageUrl) return toast.error('พิมพ์ข้อความหรือแนบรูปก่อน');
    if (!preview || preview.count === 0) return toast.error('กด "ดูจำนวนผู้รับ" ก่อนส่ง');
    if (schedOn && !schedAt) return toast.error('เลือกวันเวลาที่จะส่ง');
    let scheduled_at = null;
    if (schedOn) {
      scheduled_at = thaiLocalToISO(schedAt);
      if (new Date(scheduled_at).getTime() < Date.now() + 30000) return toast.error('เวลาที่ตั้งต้องเป็นอนาคต');
    }
    const when = schedOn ? `ตั้งเวลาส่ง ${new Date(scheduled_at).toLocaleString('th-TH')}` : 'ส่งทันที';
    if (!window.confirm(`ยืนยันส่งถึง ${preview.count} คน?\n${when}\n\nการส่งจะนับโควตา LINE และยกเลิกไม่ได้`)) return;
    setSending(true);
    try {
      await API.post('/api/broadcasts', {
        channel_id: channelId, message_text: message, image_url: imageUrl || null,
        msg_type: msgType,
        card: msgType === 'card' ? { title: cTitle, detail: cDetail, button_label: cBtnLabel, button_url: cBtnUrl }
             : msgType === 'rich' ? { link: richLink } : null,
        audience_type: audType, audience_filter: audFilter(), scheduled_at, confirm: true,
      });
      toast.success(schedOn ? 'ตั้งเวลาส่งเรียบร้อย!' : 'เริ่มส่งแล้ว!');
      setMessage(''); setImageUrl(''); setOrigFile(null); setPreview(null); setSchedOn(false); setSchedAt(''); setSelCust([]);
      setCTitle(''); setCDetail(''); setCBtnLabel(''); setCBtnUrl(''); setRichLink('');
      setTab('history'); loadHistory();
    } catch (e) { toast.error(e.response?.data?.error || 'ส่งไม่สำเร็จ'); } finally { setSending(false); }
  }

  async function doRetry(id) {
    if (!window.confirm('ส่งซ้ำถึงผู้รับที่ล้มเหลว?')) return;
    try { await API.post(`/api/broadcasts/${id}/retry`); toast.success('เริ่มส่งซ้ำแล้ว'); loadHistory(); }
    catch (e) { toast.error(e.response?.data?.error || 'ส่งซ้ำไม่สำเร็จ'); }
  }
  async function doCancel(id) {
    if (!window.confirm('ยกเลิกการส่งที่ตั้งเวลาไว้?')) return;
    try { await API.post(`/api/broadcasts/${id}/cancel`); toast.success('ยกเลิกแล้ว'); loadHistory(); }
    catch (e) { toast.error(e.response?.data?.error || 'ยกเลิกไม่สำเร็จ'); }
  }

  const toggleTag = (t) => setSelTags((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const toggleCust = (c) => setSelCust((s) => (s.find((x) => x.id === c.id) ? s.filter((x) => x.id !== c.id) : [...s, { id: c.id, name: c.name }]));
  const quotaPct = preview?.quota?.limit ? Math.min(100, Math.round((preview.quota.used / preview.quota.limit) * 100)) : null;

  return (
    <div style={{ padding: 24, maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 26 }}>📣</span>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>บรอดแคสต์</h1>
      </div>
      <p style={{ color: '#64748b', marginTop: 4, marginBottom: 20 }}>ส่งข้อความ/รูปหาลูกค้า LINE พร้อมกันหลายคน</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[['compose', '✏️ เขียนข้อความ'], ['history', '🕑 ประวัติการส่ง']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); if (k === 'history') loadHistory(); }}
            style={{ padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
              border: tab === k ? '1px solid #4f46e5' : '1px solid #e5e7eb', background: tab === k ? '#eef2ff' : '#fff', color: tab === k ? '#4f46e5' : '#475569' }}>{l}</button>
        ))}
      </div>

      {tab === 'compose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {channels.length === 0 ? (
            <div style={{ ...card, color: '#64748b' }}>ยังไม่มีช่องทาง LINE — เพิ่มช่องทาง LINE ก่อนจึงจะส่งบรอดแคสต์ได้</div>
          ) : (
            <>
              <div style={card}>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#334155', display: 'block', marginBottom: 8 }}>ช่องทาง LINE</label>
                <select value={channelId} onChange={(e) => setChannelId(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}>
                  {channels.map((c) => (<option key={c.id} value={c.id}>💚 {c.name}{c.is_active ? '' : ' (ปิดใช้งาน)'}</option>))}
                </select>
              </div>

              <div style={card}>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#334155', display: 'block', marginBottom: 10 }}>กลุ่มผู้รับ</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {AUD.map((a) => (
                    <button key={a.key} onClick={() => setAudType(a.key)} style={{ padding: '7px 14px', borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      border: audType === a.key ? '1px solid #4f46e5' : '1px solid #e5e7eb', background: audType === a.key ? '#4f46e5' : '#fff', color: audType === a.key ? '#fff' : '#475569' }}>{a.label}</button>
                  ))}
                </div>

                {audType === 'tag' && (allTags.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>ช่องทางนี้ยังไม่มีแท็กลูกค้า</div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {allTags.map((t) => (
                      <button key={t} onClick={() => toggleTag(t)} style={{ padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        border: selTags.includes(t) ? '1px solid #16a34a' : '1px solid #e5e7eb', background: selTags.includes(t) ? '#dcfce7' : '#fff', color: selTags.includes(t) ? '#166534' : '#64748b' }}>{selTags.includes(t) ? '✓ ' : ''}{t}</button>
                    ))}
                  </div>
                ))}
                {audType === 'recent' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#475569' }}>
                    ทักมาภายใน
                    <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} style={{ width: 70, padding: 8, borderRadius: 8, border: '1px solid #cbd5e1', textAlign: 'center' }} />
                    วันล่าสุด
                  </div>
                )}
                {audType === 'manual' && (
                  <div>
                    <input value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="ค้นหาชื่อลูกค้า..."
                      style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box', marginBottom: 8 }} />
                    {selCust.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: '#4f46e5', fontWeight: 700 }}>เลือกแล้ว {selCust.length} คน</span>
                        <button onClick={() => setSelCust([])} style={smallBtn('#fecaca', '#dc2626', '#fef2f2')}>ยกเลิกทั้งหมด</button>
                      </div>
                    )}
                    <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                      {custList.length === 0 ? (
                        <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>ไม่พบลูกค้า</div>
                      ) : custList.map((c) => {
                        const on = !!selCust.find((x) => x.id === c.id);
                        return (
                          <div key={c.id} onClick={() => toggleCust(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: on ? '#eef2ff' : '#fff', borderBottom: '1px solid #f8fafc' }}>
                            <input type="checkbox" readOnly checked={on} />
                            {c.profile_pic ? <img src={c.profile_pic} alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} /> : <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0' }} />}
                            <span style={{ fontSize: 14, color: '#334155' }}>{c.name || '(ไม่มีชื่อ)'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div style={card}>
                {/* mode toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[['text', '💬 ข้อความ'], ['card', '🃏 การ์ด (รูป+ปุ่ม)'], ['rich', '🖼️ ริชเมสเสจ']].map(([k, l]) => (
                    <button key={k} onClick={() => { setMsgType(k); setPreview(null); }}
                      style={{ padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', flex: 1, minWidth: 100,
                        border: msgType === k ? '1px solid #4f46e5' : '1px solid #e5e7eb', background: msgType === k ? '#eef2ff' : '#fff', color: msgType === k ? '#4f46e5' : '#475569' }}>{l}</button>
                  ))}
                </div>

                {msgType === 'text' && (
                  <>
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} maxLength={5000} placeholder="พิมพ์ข้อความที่จะส่งถึงลูกค้า..."
                      style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{message.length}/5000</div>
                  </>
                )}
                {msgType === 'card' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input value={cTitle} onChange={(e) => setCTitle(e.target.value)} maxLength={160} placeholder="หัวข้อการ์ด (ตัวหนา)"
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, fontWeight: 700, boxSizing: 'border-box' }} />
                    <textarea value={cDetail} onChange={(e) => setCDetail(e.target.value)} rows={3} maxLength={1000} placeholder="รายละเอียด"
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={cBtnLabel} onChange={(e) => setCBtnLabel(e.target.value)} maxLength={40} placeholder="ข้อความปุ่ม เช่น ดูโปร"
                        style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }} />
                      <input value={cBtnUrl} onChange={(e) => setCBtnUrl(e.target.value)} placeholder="ลิงก์ปุ่ม https://..."
                        style={{ flex: 1.4, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>ปุ่มลิงก์จะนับยอดคลิกให้อัตโนมัติ (ดูได้ในประวัติ)</div>
                  </div>
                )}
                {msgType === 'rich' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                      🖼️ รูปเต็มความกว้างแชท · ลูกค้า<b>คัดลอกข้อความไม่ได้</b> (เป็นรูป) · กดที่รูปเปิดลิงก์ได้ — เหมาะกับโปรโมชัน
                    </div>
                    <input value={richLink} onChange={(e) => setRichLink(e.target.value)} placeholder="ลิงก์เมื่อกดที่รูป (ไม่บังคับ) https://..."
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }} />
                    <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={400} placeholder="ข้อความแจ้งเตือน (แสดงตอนเปิดรูปไม่ได้ เช่นบนหน้า Lock screen)"
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                )}

                {/* image (hero for card, image message for text) */}
                <div style={{ marginTop: 12 }}>
                  {imageUrl ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <img src={imageUrl} alt="" style={{ width: msgType === 'rich' ? 150 : msgType === 'card' ? 120 : 64, height: msgType === 'rich' ? 'auto' : msgType === 'card' ? 78 : 64, maxHeight: msgType === 'rich' ? 220 : undefined, objectFit: msgType === 'rich' ? 'contain' : 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      {msgType === 'card' && origFile && (
                        <button onClick={() => setCropFile(origFile)} style={smallBtn('#bae6fd', '#0369a1', '#f0f9ff')}>✂️ ครอปใหม่</button>
                      )}
                      <button onClick={() => { setImageUrl(''); setOrigFile(null); }} style={smallBtn('#fecaca', '#dc2626', '#fef2f2')}>ลบรูป</button>
                    </div>
                  ) : (
                    <div>
                      <label style={{ ...smallBtn('#cbd5e1', '#475569', '#f8fafc'), display: 'inline-block' }}>
                        {uploading ? 'กำลังอัปโหลด...' : (msgType === 'card' ? '🖼️ รูปหัวการ์ด (JPG/PNG)' : msgType === 'rich' ? '🖼️ รูปริชเมสเสจ (เต็มจอ JPG/PNG)' : '🖼️ แนบรูป (JPG/PNG)')}
                        <input type="file" accept="image/png,image/jpeg" onChange={onImage} disabled={uploading} style={{ display: 'none' }} />
                      </label>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                        {msgType === 'card'
                          ? '📐 แนะนำ 1040 × 676 px (สัดส่วน 20:13) · เลือกรูปแล้วปรับ/ครอปได้ก่อนส่ง'
                          : msgType === 'rich'
                          ? '📐 รูปเต็มความกว้าง · แนะนำกว้าง 1040px (สูงได้ตามต้องการ) · ระบบย่อให้ LINE อัตโนมัติ · ไม่ครอป'
                          : '📐 JPG/PNG · แนะนำกว้างไม่เกิน 1040px'}
                      </div>
                    </div>
                  )}
                </div>

                {/* img_crop_v1: หน้าต่างครอปรูปการ์ด 20:13 */}
                {cropFile && (
                  <ImageCropModal
                    file={cropFile}
                    aspect={20 / 13}
                    outWidth={1040}
                    onCancel={() => setCropFile(null)}
                    onDone={onCropped}
                  />
                )}

                {/* rich_msg_v1: rich message live preview */}
                {msgType === 'rich' && imageUrl && (
                  <div style={{ marginTop: 14, maxWidth: 260 }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>ตัวอย่างในแชท:</div>
                    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                      <img src={imageUrl} alt="" style={{ width: '100%', display: 'block' }} />
                      {richLink.trim() && (
                        <div style={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(15,23,42,0.75)', color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 20 }}>🔗 กดเปิดลิงก์</div>
                      )}
                    </div>
                  </div>
                )}

                {/* card live preview */}
                {msgType === 'card' && (cTitle || cDetail || imageUrl || cBtnLabel) && (
                  <div style={{ marginTop: 14, maxWidth: 260, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
                    {imageUrl && <img src={imageUrl} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />}
                    <div style={{ padding: 12 }}>
                      {cTitle && <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{cTitle}</div>}
                      {cDetail && <div style={{ fontSize: 13, color: '#555', marginTop: 4, whiteSpace: 'pre-wrap' }}>{cDetail}</div>}
                      {cBtnLabel && <div style={{ marginTop: 10, textAlign: 'center', background: '#4f46e5', color: '#fff', borderRadius: 8, padding: '8px 0', fontWeight: 700, fontSize: 14 }}>{cBtnLabel}</div>}
                    </div>
                  </div>
                )}
              </div>

              {/* schedule */}
              <div style={card}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                  <input type="checkbox" checked={schedOn} onChange={(e) => setSchedOn(e.target.checked)} />
                  ตั้งเวลาส่ง (ไม่เลือก = ส่งทันที)
                </label>
                {schedOn && (
                  <input type="datetime-local" value={schedAt} min={nowThaiLocal()} onChange={(e) => setSchedAt(e.target.value)}
                    style={{ marginTop: 10, padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} />
                )}
              </div>

              {preview && (
                <div style={{ ...card, background: '#f8fafc' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>จะส่งถึง <span style={{ color: '#4f46e5' }}>{preview.count}</span> คน</div>
                  {preview.sample_names?.length > 0 && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>เช่น {preview.sample_names.join(', ')}{preview.count > preview.sample_names.length ? ' ...' : ''}</div>
                  )}
                  {quotaPct != null && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                        โควตา LINE เดือนนี้: {preview.quota.used.toLocaleString()} / {preview.quota.limit.toLocaleString()}
                        {preview.quota.remaining != null && preview.count > preview.quota.remaining && (<span style={{ color: '#dc2626', fontWeight: 700 }}> — โควตาอาจไม่พอ!</span>)}
                      </div>
                      <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: `${quotaPct}%`, height: '100%', background: quotaPct > 90 ? '#dc2626' : '#4f46e5' }} /></div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={doPreview} disabled={previewing} style={btn('#0ea5e9', previewing)}>{previewing ? 'กำลังนับ...' : '👥 ดูจำนวนผู้รับ'}</button>
                <button onClick={doSend} disabled={sending || !preview} style={btn(schedOn ? '#7c3aed' : '#16a34a', sending || !preview)}>
                  {sending ? 'กำลังส่ง...' : (schedOn ? '🕒 ตั้งเวลาส่ง' : '🚀 ส่งเลย')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {history.length === 0 ? (
            <div style={{ ...card, color: '#64748b', textAlign: 'center' }}>ยังไม่มีประวัติการส่ง</div>
          ) : history.map((b) => {
            const s = STATUS_BADGE[b.status] || STATUS_BADGE.pending;
            const pct = b.total_targets ? Math.round((b.sent_count / b.total_targets) * 100) : 0;
            return (
              <div key={b.id} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {b.msg_type === 'card'
                        ? '🃏 ' + ((b.card && b.card.title) || (b.card && b.card.detail) || '(การ์ด)')
                        : (b.image_url ? '🖼️ ' : '') + (b.message_text || (b.image_url ? '(รูปภาพ)' : ''))}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                      💚 {b.channel_name || '-'} · {new Date(b.created_at).toLocaleString('th-TH')}
                      {b.status === 'scheduled' && b.scheduled_at && <span style={{ color: '#7c3aed', fontWeight: 700 }}> · ส่ง {new Date(b.scheduled_at).toLocaleString('th-TH')}</span>}
                    </div>
                  </div>
                  <span style={{ background: s.b, color: s.c, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.t}</span>
                </div>
                {b.status !== 'scheduled' && b.status !== 'cancelled' && (
                  <div style={{ marginTop: 10, fontSize: 13, color: '#475569' }}>
                    ส่งสำเร็จ {b.sent_count}/{b.total_targets}{b.fail_count > 0 && <span style={{ color: '#dc2626' }}> · ล้มเหลว {b.fail_count}</span>}
                    {b.msg_type === 'card' && b.card && b.card.button_url && <span style={{ color: '#4f46e5', fontWeight: 700 }}> · 👆 คลิก {b.click_count || 0} ครั้ง</span>}
                  </div>
                )}
                {(b.status === 'sending' || b.status === 'pending') && (
                  <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginTop: 6 }}><div style={{ width: `${pct}%`, height: '100%', background: '#4f46e5', transition: 'width .3s' }} /></div>
                )}
                {(b.status === 'partial' || b.status === 'failed') && b.fail_count > 0 && (
                  <div style={{ marginTop: 10 }}><button onClick={() => doRetry(b.id)} style={smallBtn('#fed7aa', '#9a3412', '#fff7ed')}>🔁 ส่งซ้ำที่ล้มเหลว ({b.fail_count})</button></div>
                )}
                {b.status === 'scheduled' && (
                  <div style={{ marginTop: 10 }}><button onClick={() => doCancel(b.id)} style={smallBtn('#e2e8f0', '#475569', '#f8fafc')}>ยกเลิก</button></div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
